import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import {
  Box,
  Button,
  Typography,
  TextField,
  Avatar,
  Grid,
  useMediaQuery,
} from '@mui/material';
import publicRegistration from '../../images/public-registration.png';
import AdvisoryCloudLogo from '../AdvisoryCloudLogo';

const useStyles = () => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    paddingTop: '20px',
  },
  bodyText: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#2E384D',
  },
  input: {
    width: '340px',
    borderRadius: '10px',
    backgroundColor: '#F5F7FA',
    height: '44px',
    marginBottom: '14px',
    border: '1px solid #C7D0DE',
    '& .MuiOutlinedInput-root': {
      height: '44px',
    },
    fieldset: {
      border: 'none',
    },
    input: {
      '&:-webkit-autofill': {
        transitionDelay: '9999s',
        transitionProperty: 'background-color, color, height, width',
      },
    },
  },
  label: {
    color: '#97A3B7',
    fontSize: '14px',
    fontWeight: '500',
    top: '-5px',
  },
  button: {
    width: '340px',
    height: '44px',
    borderRadius: '50px',
    marginTop: '10px',
    fontWeight: '700',
    fontSize: '14px',
    backgroundColor: '#3171F6',
    boxShadow: '0px 8px 15px 0px #E6EDFE26',
    '&:hover': {
      backgroundColor: '#3171F6',
      boxShadow: '0px 8px 15px 0px #E6EDFE26',
    },
    '&:disabled': {
      backgroundColor: '#E0E0E0',
      boxShadow: '0px 8px 15px 0px #E6EDFE26',
      opacity: '1',
    },
  },
});

const perks = [
  {
    title: 'Add to Resume',
    description:
      'The ability to add to your resume or biography that you are on the advisory board. Almost all offer this for being on their board.',
    image: require('../../images/add-to-resume.png'),
  },
  {
    title: 'Advisor Rate Opportunity',
    description:
      'Opportunity to be hired at your Advisor Rate if your ideas shared in the digital boardroom warrant additional insights or a specific project.',
    image: require('../../images/advisor-rate-opportunity.png'),
  },
  {
    title: 'Potential Equity',
    description:
      'Some startups agree to pledge up to 1% equity to their advisory board, split between board members based on tenure and participation.',
    image: require('../../images/potential-equity.png'),
  },
  {
    title: 'Potential Compensation Bonus',
    description:
      'If one of the ideas shared in a boardroom is deemed a “Golden Idea” some boards may offer a bonus of up to $1,000.',
    image: require('../../images/potential-compensation-bonus.png'),
  },
  {
    title: 'Free Product / Service Discount',
    description:
      'Receive a complimentary product, service or gift certificate for being on the advisory board of a particular company.',
    image: require('../../images/free-product-discount.png'),
  },
  {
    title: 'Custom Perks & Benefits',
    description:
      'Some boards offer a hat, a plaque, or even a letter of recommendation. Each board offers their own unique benefits and perks.',
    image: require('../../images/custom-perks.png'),
  },
];

const AdvisorPublicJoinForm = ({
  onSubmit,
  companySlug,
  image,
  boardOwnerName,
}) => {
  const classes = useStyles();
  const [loading, setIsLoading] = useState(false);
  const location = useLocation();
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  const [formData, setFormData] = useState({
    givenName: '',
    surName: '',
    email: '',
  });
  const { givenName, surName, email } = formData;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = () => {
    setIsLoading(true);
    if (givenName && surName && email) {
      onSubmit(formData, setIsLoading);
    }
  };

  function getText() {
    if (location.pathname.includes('advisor')) {
      return `You've been invited to the ${companySlug} Advisory Board. Fill out the information below to get access, share your ideas, and receive benefits and perks for being on this advisory board before the invitation link expires.`;
    }
    return "You've been invited to help manage ";
  }

  const isFormValid = givenName && surName && email;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get('email');
    if (emailParam) {
      setFormData((prevData) => ({ ...prevData, email: emailParam }));
    }
  }, [location.search]);

  return (
    <Box sx={classes.root}>
      {image && image.id && (
        <Avatar
          src={`${process.env.REACT_APP_IMAGES_URL}${image.id}`}
          sx={{ width: '120px', height: '120px', marginBottom: '10px' }}
        />
      )}
      <Typography variant="h2" sx={{ fontSize: '28px', fontWeight: '700' }}>
        Welcome!
      </Typography>
      {location.pathname.includes('advisor') ? (
        <Box
          margin="16px 0 32px"
          sx={{ width: isXS ? '340px' : null, maxWidth: '600px' }}
        >
          <Typography sx={classes.bodyText}>{getText()}</Typography>
        </Box>
      ) : (
        <Box margin="16px 0 32px" sx={{ width: isXS ? '340px' : null }}>
          <Typography sx={classes.bodyText}>
            {getText()}
            <span style={{ fontWeight: '700' }}>{companySlug}'s</span>
          </Typography>
          <Typography sx={classes.bodyText}>
            AdvisoryCloud. Fill out the information below to get access.
          </Typography>
        </Box>
      )}
      <TextField
        sx={classes.input}
        InputLabelProps={{
          style: classes.label,
        }}
        label="First name"
        name="givenName"
        value={givenName}
        onChange={handleChange}
      />
      <TextField
        sx={classes.input}
        InputLabelProps={{
          style: classes.label,
        }}
        label="Last name"
        name="surName"
        value={surName}
        onChange={handleChange}
      />
      <TextField
        sx={classes.input}
        InputLabelProps={{
          style: classes.label,
        }}
        label="Email"
        name="email"
        value={email}
        onChange={handleChange}
      />
      <Button
        sx={classes.button}
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={!isFormValid || loading}
      >
        {loading ? <LoadingLogoSkeleton /> : 'Continue'}
      </Button>

      <Box
        sx={{
          marginTop: isXS ? '50px' : '100px',
          maxWidth: '800px',
        }}
      >
        <img
          // src/images/public-registration.png
          src={publicRegistration}
          style={{ width: isXS ? '90%' : '100%', verticalAlign: 'middle' }}
        />
        <Box
          sx={{
            marginTop: isXS ? '0px' : '40px',
            mb: isXS ? '-40px' : '0px',
          }}
        >
          <AdvisoryCloudLogo
            removeHyperlink
            onClick={() => {
              window.location.assign('https://www.advisorycloud.com');
            }}
            width={160}
          />
        </Box>
        <Typography
          sx={{
            fontSize: isXS ? '16px' : '28px',
            fontWeight: '700',
            mb: isXS ? '15px' : '30px',
            mx: '10px',
          }}
        >
          Benefits & Perks for Board Members on AdvisoryCloud May Include:
        </Typography>
        <Grid container spacing={4} mt={2} sx={{ px: 1 }}>
          {perks.map((perk, index) => (
            <Grid item xs={isXS ? 12 : 6} sm={6} key={index}>
              <Box
                display="flex"
                alignItems="flex-start"
                sx={{
                  px: isXS ? 2 : 0,
                }}
              >
                <Box
                  component="img"
                  src={perk.image}
                  alt={perk.title}
                  sx={{
                    width: isXS ? 50 : 75,
                    height: isXS ? 50 : 75,
                    mr: isXS ? 2 : 4,
                    mt: 0.5,
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      lineHeight: '24px',
                      fontWeight: '700',
                      color: '#646D7A',
                      textAlign: 'left',
                      // marginBottom: '4px',
                    }}
                  >
                    {perk.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      lineHeight: '22px',
                      fontWeight: '500',
                      color: '#646D7A',
                      textAlign: 'left',
                      // marginBottom: '4px',
                    }}
                  >
                    {perk.description}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default AdvisorPublicJoinForm;
